/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicCrudScreenType from "../screenTypes/functional/BasicCrudScreenType";
import getSchema from "../../lib/api/getSchema";
import apiClient from "../../lib/common/apiClient";
import BasicCrudScreenActionItemLayout from "../screenTypes/layout/BasicCrudScreenActionItemLayout";
import { DialogContent, DialogTitle } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import AutoForm from "../form/functional/AutoForm";
import { ReflexContainer, ReflexSplitter, ReflexElement } from "react-reflex";
import layoutConf from "../../config/layoutConf";
import CustomSelector from "../input/CustomSelector";

/**
 * @COMPONENT_NAME ApiHistory
 * @DESCRIPTION API 호출 이력을 확인할 수 있는 화면입니다. PATCH나 PUT 메소드의 경우 
 * 				data1과 data2를 비교할 수 있습니다. data1은 이전 api 호출이며 data2는 이후 api 호출입니다.
 *
 * @PROPS
	  - 없음
 * @PASSTHROUGH_PROPS
 *   - {string} id - "ApiHistory";
 *   - {string} view - "VwApiHistory"
 *   - {string} table - "ApiHistory"
 * @CALLBACKS
 *	  - {Function} onApiInitialized - API 초기화 시 호출되는 콜백 함수
 *	  - {Function} onInitialized - grid 초기화 시 호출되는 콜백 함수
 *	  - {Function} onChangeData1 - data1 변경 시 호출되는 콜백 함수
 *	  - {Function} onChangeData2 - data2 변경 시 호출되는 콜백 함수
 * 
 */
function safeParse(jsonStr) {
	if (!jsonStr || typeof jsonStr !== "string") return null;
	try {
		return JSON.parse(jsonStr);
	} catch {
		return null;
	}
}

function ApiHistory(props) {
	const { t } = useTranslation();
	const id = "ApiHistory";
	const table = "ApiHistory";
	const view = "VwApiHistory";

	const [schema, setSchema] = useState();
	const [api, setApi] = useState();
	const [grid, setGrid] = useState();
	const [apiArray, setApiArray] = useState([]);
	const [selectedTargetName, setSelectedTargetName] = useState();
	const [selectedTableSchema, setSelectedTableSchema] = useState(null);
	const [data1, setData1] = useState(null);
	const [data2, setData2] = useState(null);
	const [isPatch, setIsPatch] = useState(false);
	// Clear data1 and data2 when selectedTargetName changes
	useEffect(() => {
		setData1(null);
		setData2(null);
		setSelectedTableSchema(null);
		setIsPatch(false);
	}, [selectedTargetName]);

	const [height, setHeight] = useState(0);
	const handleResize = () => {
		setHeight(document.documentElement.offsetHeight - 96);
	};
	useEffect(() => {
		window.addEventListener("resize", handleResize);

		handleResize();

		return () => window.removeEventListener("resize", handleResize);
	}, []);
	useEffect(() => {
		(async () => {
			let response = await apiClient.get("/api/ApiHistory");
			const list = Array.isArray(response) ? response : response?.data;
			if (Array.isArray(list)) {
				const selected = list.map((item) => item.targetName);
				setApiArray(["all", ...selected]);
				setSelectedTargetName("all");
			} else {
				setApiArray([]);
			}
		})();
	}, []);
	useEffect(() => {
		let mounted = true;

		async function schemaSetting() {
			let schema = await getSchema(table);

			if (mounted) {
				setSchema(schema);
			}
		}

		schemaSetting();
		return () => {
			mounted = false;
		};
	}, []);
	function makeSchema(data) {
		if (!data) return {};
		const schema = {
			columns: Object.keys(data).map((key) => ({
				name: key,
				formItemType: "text",
				readonly: true,
			})),
		};
		setSelectedTableSchema(schema);
	}
	useEffect(() => {
		(async () => {
			if (grid) {
				const showSelected = () => {
					if (grid.selectedItems && grid.selectedItems[0]) {
						let item = grid.selectedItems[0];
						setData1(safeParse(item?.data1));
						setData2(safeParse(item?.data2));
						makeSchema(safeParse(item?.data1));
						setIsPatch(
							item.method === "PATCH" || item.method === "PUT"
						);
					}
				};
				grid.selectionChanged.addHandler(showSelected);
				// 목록을 읽으면 첫 줄이 골라진 채 그려진다 — 고르기 이벤트 없이도 그 줄의 이력을 보인다
				grid.loadedRows.addHandler(showSelected);
				showSelected();
			}
		})();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [grid, selectedTargetName]);

	useEffect(() => {
		if (api) api.refresh();
	}, [schema]);

	const onApiInitialized = (api) => {
		setApi(api);
	};
	const onInitialized = (grid) => {
		setGrid(grid);
	};
	const onChangeData1 = (data) => {
		setData1(data);
	};
	const onChangeData2 = (data) => {
		setData2(data);
	};
	useEffect(() => {
		if (api) api.refresh();
		setSelectedTableSchema(null);
		setIsPatch(false);
	}, [selectedTargetName]);
	return (
		<div style={{ height: height }}>
			<ReflexContainer orientation="vertical">
				<ReflexElement flex={0.5} data-cy="ApiHistoryGrid">
					<BasicCrudScreenType
						id={id}
						headerTitle={t("term.apiHistory")}
						table={table}
						view={view}
						onModifyViewUrl={(url) => `${url}/${selectedTargetName}`}
						noCreate
						noEdit
						noDelete
						noExcel
						onApiInitialized={onApiInitialized}
						onInitialized={onInitialized}
						onAppendButtonInFrontOfSearch={() => (
							<BasicCrudScreenActionItemLayout>
								<CustomSelector
									value={selectedTargetName}
									setValue={setSelectedTargetName}
									apiArray={apiArray}
								/>
							</BasicCrudScreenActionItemLayout>
						)}
					/>
				</ReflexElement>
				<ReflexSplitter
					style={{ width: layoutConf.reflex.splitterWidth }}
				/>
				<ReflexElement flex={0.5}>
					<ReflexContainer orientation="horizontal">
						<ReflexElement flex={0.5} data-cy="ApiHistoryData1">
							<DialogTitle>
								{t("term.prevApiHistory")}
							</DialogTitle>
							<DialogContent>
								<Paper
									elevation={2}
									style={{ padding: 16, minHeight: 120 }}
								>
									{data1 ? (
										<AutoForm
											schema={selectedTableSchema}
											initialData={data1}
											mode="edit"
											onChange={onChangeData1}
										/>
									) : null}
								</Paper>
							</DialogContent>
						</ReflexElement>
						<ReflexSplitter
							style={{ height: layoutConf.reflex.splitterHeight }}
						/>
						<ReflexElement flex={0.5} data-cy="ApiHistoryData2">
							{isPatch ? (
								<>
									<DialogTitle>
										{t("term.nextApiHistory")}
									</DialogTitle>
									<DialogContent>
										<Paper
											elevation={2}
											style={{
												padding: 16,
												minHeight: 120,
											}}
										>
											{data2 ? (
												<AutoForm
													schema={selectedTableSchema}
													initialData={data2}
													mode="edit"
													onChange={onChangeData2}
												/>
											) : null}
										</Paper>
									</DialogContent>
								</>
							) : null}
						</ReflexElement>
					</ReflexContainer>
				</ReflexElement>
			</ReflexContainer>
		</div>
	);
}

export default ApiHistory;
